import { ApiError } from "#/client";
import { Button } from "#/components/button/button";
import { Input } from "#/components/input/input";
import { confirmTotp, enrollTotp } from "#/feature/auth/auth-api";
import type { User } from "#/feature/auth/auth-store";
import { userSignal } from "#/feature/auth/auth-store";
import { QRCodeSVG } from "qrcode.react";
import React, { useCallback, useEffect, useState } from "react";

import {
  buttonStyles,
  errorStyles,
  fieldStyles,
  formStyles,
  inputStyles,
  labelStyles,
} from "./login.css";

interface TotpSetupProps {
  mode: "add" | "update";
  onComplete?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

export function TotpSetup({ mode, onComplete, onCancel, showCancel = true }: TotpSetupProps) {
  const [step, setStep] = useState<"current" | "setup">(mode === "update" ? "current" : "setup");
  const [currentCode, setCurrentCode] = useState("");
  const [code, setCode] = useState("");
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const startEnroll = useCallback(async (existingCode?: string) => {
    setError("");
    setLoading(true);
    try {
      const res = await enrollTotp(existingCode);
      setOtpauthUri(res.otpauth_uri);
      setSecret(res.secret);
      setStep("setup");
    } catch (err) {
      if (err instanceof ApiError && err.response.status === 401) {
        setError("Invalid authenticator code");
      } else {
        setError("Failed to start authenticator setup");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === "add" && step === "setup" && !otpauthUri) {
      void startEnroll();
    }
  }, [mode, step, otpauthUri, startEnroll]);

  const refreshUser = (updated: User) => {
    userSignal(updated);
  };

  const handleCurrentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await startEnroll(currentCode);
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const updated = await confirmTotp(code);
      refreshUser(updated);
      onComplete?.();
    } catch (err) {
      if (err instanceof ApiError && err.response.status === 401) {
        setError("Invalid code — try again");
      } else {
        setError("Failed to confirm authenticator");
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === "current") {
    return (
      <form onSubmit={handleCurrentSubmit} className={formStyles}>
        {error && <div className={errorStyles}>{error}</div>}
        <p style={{ fontSize: "0.875rem", marginBottom: "0.5rem" }}>
          Enter your current authenticator code to continue.
        </p>
        <div className={fieldStyles}>
          <label className={labelStyles}>Current code</label>
          <Input
            className={inputStyles}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={currentCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentCode(e.target.value)}
            required
          />
        </div>
        <Button type="submit" fullWidth className={buttonStyles} disabled={loading}>
          Continue
        </Button>
        {showCancel && onCancel && (
          <Button type="button" variant="outline" fullWidth onClick={onCancel}>
            Cancel
          </Button>
        )}
      </form>
    );
  }

  if (step === "setup" && !otpauthUri) {
    return <div style={{ textAlign: "center", padding: "1rem" }}>Preparing setup…</div>;
  }

  return (
    <form onSubmit={handleConfirm} className={formStyles}>
      {error && <div className={errorStyles}>{error}</div>}
      {otpauthUri && (
        <div
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "0.5rem",
              borderRadius: "0.5rem",
            }}
          >
            <QRCodeSVG value={otpauthUri} size={180} />
          </div>
          {secret && (
            <div style={{ width: "100%" }}>
              <p style={{ fontSize: "0.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>
                Can&apos;t scan? Enter this key in your app:
              </p>
              <code
                style={{
                  display: "block",
                  wordBreak: "break-all",
                  fontSize: "0.875rem",
                  padding: "0.5rem",
                  backgroundColor: "var(--colors-bg-base100)",
                  border: "1px solid var(--colors-bg-base300)",
                  borderRadius: "0.5rem",
                }}
              >
                {secret}
              </code>
            </div>
          )}
        </div>
      )}
      <div className={fieldStyles}>
        <label className={labelStyles}>Verification code</label>
        <Input
          className={inputStyles}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
          required
        />
      </div>
      <Button type="submit" fullWidth className={buttonStyles} disabled={loading}>
        Confirm
      </Button>
      {showCancel && onCancel && (
        <Button type="button" variant="outline" fullWidth onClick={onCancel}>
          {mode === "add" ? "Skip for now" : "Cancel"}
        </Button>
      )}
    </form>
  );
}
